import { configureStore } from "@reduxjs/toolkit";
import { combineReducers } from "redux";
import { persistStore, persistReducer, createTransform } from "redux-persist";
import storage from "redux-persist/lib/storage";

import themeReducer from "./reducers/ThemeReducer";
import authReducer from "./reducers/AuthReducer";
import candidateReducer from "./reducers/CandidateReducer";
import interviewReducer from "./reducers/InterviewReducer";
import webSocketReducer from "./reducers/WebSocketReducer";
import sequencesReducer from "./reducers/SequenceReducer";
// Interview Scheduling, Sourcing and Extension are disabled — commented
// out, not deleted. See PLAN doc "resilient-stirring-tulip".
// import interviewSchedulingReducer from "./reducers/InterviewSchedulingReducer";
// import campaignReducer from "./reducers/CampaignReducer";
// import scraperReducer from "./reducers/ScraperReducer";

/**
 * Only `auth` is persisted, and only the parts of it that describe *who* the
 * user is.
 *
 * The transient flags are stripped on the way out. They describe a request
 * that was in flight when the tab closed, and rehydrating them replays a
 * moment that has passed: a reload during sign-in used to come back with
 * `isLoading: true`, which every guard reads as "keep showing the spinner",
 * and a reload after a failed call came back with a stale `error` that the
 * next screen reading `auth.error` would display out of nowhere.
 *
 * `authChecked` is stripped for a sharper reason: it means "we have asked the
 * server who this is *this page load*". Persisting it would let the guards act
 * on a rehydrated session before bootstrap has confirmed it still exists.
 */
const stripTransientAuthState = createTransform(
  (inbound) => ({ isAuthenticated: inbound.isAuthenticated, user: inbound.user }),
  (outbound) => ({
    ...outbound,
    isLoading: false,
    isRefreshing: false,
    authChecked: false,
    error: null,
  }),
  { whitelist: ["auth"] },
);

// Configure persist
const persistConfig = {
  key: "talentflow",
  // `redux-persist/lib/storage` is CJS. The app's pre-bundled build hands back
  // the module namespace, where the engine sits on `.default`; under vitest the
  // dep is externalized and the same import hands back the engine itself. Take
  // whichever arrived — reading only `.default` threw "config.storage is
  // required" in every test whose page reaches this store.
  storage: storage.default ?? storage,
  whitelist: ["auth"],
  transforms: [stripTransientAuthState],
};

// Combine reducers into rootReducer
const rootReducer = combineReducers({
  theme: themeReducer,
  auth: authReducer,
  candidateAnalysis: candidateReducer,
  interview: interviewReducer,
  webSocket: webSocketReducer,
  sequences: sequencesReducer,
  // Interview Scheduling, Sourcing and Extension are disabled — commented
  // out, not deleted.
  // interviewScheduling: interviewSchedulingReducer,
  // // Sourcing — not in `whitelist` above, so neither survives a reload: both
  // // hold server state (campaign list, live scrape progress) that must be
  // // re-fetched rather than rehydrated stale.
  // campaigns: campaignReducer,
  // scraper: scraperReducer,
});

// Wrap rootReducer with persistReducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Configure the store without redux-persist
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ["persist/PERSIST", "persist/REHYDRATE"],
      },
    }),
  devTools: JSON.parse(import.meta.env.VITE_REDUX_DEVTOOL),
});

export function setupStore(preloadedState) {
  return configureStore({
    reducer: rootReducer,
    preloadedState,
  });
}

export const persistor = persistStore(store);
